import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { EmptyState } from "@/core/ui";

const formatEndsAt = (endsAt) =>
  new Date(endsAt).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const getEndsAtColor = (endsAt) => {
  const daysLeft = Math.ceil((new Date(endsAt) - new Date()) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 2) return "error.main";
  if (daysLeft <= 5) return "warning.main";
  return "text.secondary";
};

const CurrentMatch = ({
  matches,
  isWithoutSeason,
  emptySeasonMessage,
  isEditingResults,
  resultDraft,
  handleDraftChange,
  canSubmitAllResults,
  hasEditableMatches,
  hasSubmittedResults,
  handleEditResults,
  handleSaveResults,
  loading,
  error,
  roundEndsAt,
}) => {
  const isMatchesLoading = loading?.matches === true;
  const isSaving = loading?.savingResults === true;
  const saveError = error?.type === "saveResults" ? error.message : null;

  if (isMatchesLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
        }}
      >
        <Skeleton variant="text" width={130} height={34} />
        <Skeleton variant="text" width={170} height={24} sx={{ mb: 1.5 }} />

        <Stack spacing={1.2}>
          {[1, 2, 3].map((item) => (
            <Paper
              key={`match-skeleton-${item}`}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box>
                  <Skeleton variant="text" width={90} height={24} />
                  <Skeleton variant="text" width={90} height={24} />
                </Box>

                <Skeleton variant="text" width={42} height={42} />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <Skeleton variant="text" width={90} height={24} />
                  <Skeleton variant="text" width={90} height={24} />
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Skeleton variant="rounded" width={220} height={36} />
        </Box>
      </Paper>
    );
  }

  if (isWithoutSeason) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2.5,
          background:
            "linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Rodada Atual
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seus próximos jogos aparecerão aqui
        </Typography>
        <EmptyState
          icon={<SportsTennisIcon sx={{ fontSize: 34 }} />}
          title="Nenhum ranking ativo para você"
          description={emptySeasonMessage}
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Rodada Atual
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Seus próximos 3 jogos
      </Typography>
      {roundEndsAt && (
        <Typography
          variant="caption"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, color: getEndsAtColor(roundEndsAt) }}
        >
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          Encerra em {formatEndsAt(roundEndsAt)}
        </Typography>
      )}

      <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
        {matches.map((match) => (
          <Box
            key={match.id}
            sx={{
              py: 1.5,
              opacity: match.isCancelled ? 0.72 : 1,
            }}
          >
            {match.isCancelled && (
              <Chip
                size="small"
                label="Cancelada"
                color="warning"
                variant="outlined"
                sx={{ mb: 1, fontWeight: 800 }}
              />
            )}
            {/* Players section */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", mb: 1 }}>
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                {match.teamA.map((player, idx) => (
                  <Box
                    key={idx}
                    sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
                  >
                    <Avatar
                      src={player.avatarUrl || undefined}
                      sx={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}
                    >
                      {player.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <Typography
                      noWrap
                      title={player.name}
                      sx={{ fontSize: "0.78rem", fontWeight: 600, minWidth: 0, lineHeight: 1.5 }}
                    >
                      {player.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                {match.teamB.map((player, idx) => (
                  <Box
                    key={idx}
                    sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, justifyContent: "flex-end" }}
                  >
                    <Typography
                      noWrap
                      title={player.name}
                      sx={{ fontSize: "0.78rem", fontWeight: 600, minWidth: 0, textAlign: "right", lineHeight: 1.5 }}
                    >
                      {player.name}
                    </Typography>
                    <Avatar
                      src={player.avatarUrl || undefined}
                      sx={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}
                    >
                      {player.name?.[0]?.toUpperCase()}
                    </Avatar>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Score section */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: 1,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: match.isCancelled ? "warning.main" : "transparent",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
              }}
            >
              <Typography noWrap sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                {match.teamA.map((p) => p.name.split(" ")[0]).join(" & ")}
              </Typography>

              {match.isCancelled ? (
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.secondary",
                    lineHeight: 1,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  Cancelada
                </Typography>
              ) : isEditingResults ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TextField
                    size="small"
                    value={resultDraft[match.id]?.teamA || ""}
                    onChange={handleDraftChange(match.id, "teamA")}
                    inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
                    sx={{ width: 48 }}
                  />
                  <Typography sx={{ fontWeight: 700 }}>x</Typography>
                  <TextField
                    size="small"
                    value={resultDraft[match.id]?.teamB || ""}
                    onChange={handleDraftChange(match.id, "teamB")}
                    inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
                    sx={{ width: 48 }}
                  />
                </Stack>
              ) : match.result ? (
                <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1, whiteSpace: "nowrap" }}>
                  {match.result.teamA} x {match.result.teamB}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 16, color: "text.secondary", lineHeight: 1, fontWeight: 600 }}>
                  vs
                </Typography>
              )}

              <Typography noWrap sx={{ fontSize: "0.7rem", color: "text.secondary", textAlign: "right" }}>
                {match.teamB.map((p) => p.name.split(" ")[0]).join(" & ")}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        {hasSubmittedResults ? (
          <Button variant="outlined" disabled sx={{ minWidth: 220 }}>
            Resultados enviados
          </Button>
        ) : !hasEditableMatches ? (
          <Button variant="outlined" disabled sx={{ minWidth: 220 }}>
            Sem resultados para lançar
          </Button>
        ) : isEditingResults ? (
          <Button
            variant="contained"
            onClick={handleSaveResults}
            disabled={!canSubmitAllResults || isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ minWidth: 220 }}
          >
            {isSaving ? "Enviando..." : "Salvar resultados"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleEditResults}
            sx={{ minWidth: 220 }}
          >
            Adicionar resultados
          </Button>
        )}
      </Box>

      {isEditingResults && !canSubmitAllResults && !saveError && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 1 }}
        >
          Preencha os jogos válidos para enviar os resultados.
        </Typography>
      )}

      {saveError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", textAlign: "center", mt: 1 }}
        >
          {saveError}
        </Typography>
      )}
    </Paper>
  );
};

export default CurrentMatch;
